import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for speech recognition using Web Speech API
 * @param {boolean} autoStart - Whether to auto-start when component mounts
 * @param {Function} onResult - Callback when speech is recognized
 * @param {Function} onError - Callback when error occurs
 * @returns {Object} Speech recognition state and controls
 */
export function useSpeechRecognition(autoStart = false, onResult = null, onError = null) {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [isSupported, setIsSupported] = useState(false);
  const recognitionRef = useRef(null);
  const shouldStopRef = useRef(false); // Flag to prevent auto-restart

  // Initialize speech recognition
  useEffect(() => {
    // Check if browser supports speech recognition
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      console.warn('Speech recognition not supported in this browser');
      setIsSupported(false);
      return;
    }

    setIsSupported(true);

    // Create recognition instance
    const recognition = new SpeechRecognition();
    recognition.continuous = false; // Changed to false to prevent auto-restart
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event) => {
      let interimText = '';
      let finalText = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcriptPiece = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalText += transcriptPiece + ' ';
        } else {
          interimText += transcriptPiece;
        }
      }

      if (finalText) {
        setTranscript(prev => prev + finalText);
        if (onResult) {
          onResult(finalText.trim());
        }
      }
      setInterimTranscript(interimText);
    };

    recognition.onerror = (event) => {
      console.error('[Speech] Recognition error:', event.error);
      setIsListening(false);
      if (onError) {
        onError(event.error);
      }
    };

    recognition.onend = () => {

      // If we didn't intentionally stop, restart to keep listening
      if (!shouldStopRef.current) {
        try {
          recognition.start();
        } catch (e) {
          setIsListening(false);
        }
      } else {
        setIsListening(false);
        shouldStopRef.current = false; // Reset flag for next time
      }
    };

    recognitionRef.current = recognition;

    // Cleanup on unmount
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [onResult, onError]);

  // Start recognition
  const startListening = useCallback(() => {
    if (!isSupported || !recognitionRef.current) {
      console.warn('[Speech] Cannot start - not supported or not initialized');
      return false;
    }

    if (isListening) {
      return true;
    }

    try {
      shouldStopRef.current = false; // Clear stop flag when starting
      recognitionRef.current.start();
      return true;
    } catch (error) {
      console.error('[Speech] Failed to start recognition:', error);
      return false;
    }
  }, [isSupported, isListening]);

  // Stop recognition
  const stopListening = useCallback(() => {
    if (!recognitionRef.current || !isListening) {
      return;
    }

    try {
      shouldStopRef.current = true; // Set flag to prevent auto-restart
      setIsListening(false); // Update state immediately for instant UI feedback
      recognitionRef.current.stop();
    } catch (error) {
      console.error('[Speech] Failed to stop recognition:', error);
    }
  }, [isListening]);

  // Reset transcript
  const resetTranscript = useCallback(() => {
    setTranscript('');
    setInterimTranscript('');
  }, []);

  // Auto-start if requested
  useEffect(() => {
    if (autoStart && isSupported) {
      startListening();
    }

    return () => {
      if (autoStart) {
        stopListening();
      }
    };
  }, [autoStart, isSupported, startListening, stopListening]);

  return {
    isSupported,
    isListening,
    transcript,
    interimTranscript,
    startListening,
    stopListening,
    resetTranscript
  };
}
